import { push } from "connected-react-router";
import { useState, useMemo } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { fullName, humanReadableDate } from "../../../app/utils";
import {
  APPLICATION_MODE_NEW,
  APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE,
  DOCUMENT_CONTENT_TYPE_PDF,
  DOCUMENT_TYPE_CRIMINAL_RECORD,
  ROUTE_PATH,
  VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT,
  COMPANY_TYPES,
  DOCUMENT_TYPE_INSURANCE_CONTINURATION
} from "../../../app/constants";
import {
  Header,
  Button,
  Paragraph,
  LineSeperator,
  MainSeparator
  // ErrorMark
} from "../../../components/RegularComponents";
import { CompanyContactInformation } from "../CompanyContactInformation";
import ReviewForm from "../../../components/ReviewForm/ReviewForm";
import TopNavigationBar from "../../TopNavigationBar";
import { setMortgageProperty } from "../../../redux/slice/primaryAgencyApplicationSlice";
import { saveBusinessLicenceApplicationData } from "../../../redux/slice/businessLicenceApplicationSlice";
import {
  DocumentViewerFileNameLink,
  DownloadFileNameLink,
} from "../../../components/DocumentViewer/DocumentFileComponent";
import { convertFileDataForUrl } from "../../../app/documentUploadUtils";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import { fetchDocumentData } from "../../../redux/slice/supportingDocumentsSlice";
import { ContentAwareness } from "./ContentAwareness";
import { ConfirmAIRSubmission } from "./ConfirmAIRSubmission";
import { ConfirmCompanyType } from "./ConfirmCompanyType";
import { ConfirmCompanyFinancialLocation } from './ConfirmCompanyFinancialLocation';
import { ConfirmAppointedAgentServiceAddress } from './ConfirmAppointedAgentServiceAddress';
import { ConfirmCompanyFiscalEndDate } from './ConfirmCompanyFiscalEndDate';
import { ConfirmCriminalRecordCheck } from './ConfirmCriminalRecordCheck';
import { ConfirmInsuranceProof } from './ConfirmInsuranceProof';
import { agencyApplicationSelector } from "../../../redux/selector/agencyApplicationSelector";

const MORTGAGE_APPLICATION_PROPERTY = "userApplicationReviewComplete";

export const MraRenewalApplicationReview = (props) => {
  const dispatch = useDispatch();
  const { agencyApplication }  = useSelector(agencyApplicationSelector);

  const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
  const [documentViewerDocument, setDocumentViewerDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const applicationType = APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE;
  const applicationMode = APPLICATION_MODE_NEW;
  const lastIssuedDate = agencyApplication?.issuedDate

  const { mraApplicationState, documents } = useSelector((state) => ({
    mraApplicationState:
      state.primaryAgencyApplication.application?.businessLicenceData ?? {},
    documents: state.primaryAgencyApplication.application?.documents ?? [],
  }));

  const uploadCriminalRecordCheck = documents.filter(
    (document) =>
      document.documentType === DOCUMENT_TYPE_CRIMINAL_RECORD &&
      document.changeType !== "DELETE"
  );

  const uploadedProofOfInsuranceContinuation = documents.filter(
    (document) => (document.documentType === DOCUMENT_TYPE_INSURANCE_CONTINURATION &&
                document.changeType !== "DELETE" && 
                (document.createdDate ? document.createdDate > lastIssuedDate : true))
  );


  // const uploadedAIRDocument = documents.filter(
  //   (document) =>
  //     document.documentType === DOCUMENT_TYPE_ANNUAL_INFORMATION_RETURN &&
  //     document.changeType !== "DELETE"
  // );

  const {
    financialRecordsLocation,
    responsibleAgent,
    serviceBusinessAddress,
    companyFiscalEndDate,
    companyRjscDetails,
    mraRenewalCrcNoChangeIds,
    mraRenewalCrcUpdatedIds
  } = mraApplicationState;

  const {
    businessData,
  } = companyRjscDetails;

  const peopleList = useMemo(() => {
    if (businessData?.peopleList) {
      return businessData?.peopleList;
    }

    return [];
  }, [businessData]);

  const [editComponent, setEditComponent] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const COMPONENT_KEYS = {
    MRA_COMPLIANCE : "mraCompliance",
    AIR_REPORT : "airReport",
    COMPANY_TYPE : "companyType",
    FINANCIAL_LOCATION: "financialLocaiton",
    APPOINTED_AGENT: "appointedAgent",
    FISCAL_END_DATE: "ficalEndDate",
    CRIMINAL_RECORD: "criminalRecord",
    PROOF_INSURANCE_OMISSIONS: "proofInsuranceOmissions",
  };

  const formatDate = (date) => {
    const months = {
      0: "January",
      1: "February",
      2: "March",
      3: "April",
      4: "May",
      5: "June",
      6: "July",
      7: "August",
      8: "September",
      9: "October",
      10: "November",
      11: "December",
    };

    const dateObj = new Date(date);

    return `${months[dateObj.getMonth()]} ${dateObj.getDate()}`;
  };

  const SubSeparator = () => {
    return (
      <hr
        className="bottomMarginNormal"
        style={{
          borderColor: "#0058a466",
          borderWidth: "2px",
          width: "70%",
          marginLeft: "0px",
        }}
      />
    );
  };

  const titleForComponent = (componentKey) => {
    switch (componentKey) {
      case COMPONENT_KEYS.MRA_COMPLIANCE:
        return "MRA Compliance";
      case COMPONENT_KEYS.AIR_REPORT:
        return "Annual Information Return (AIR)";
      case COMPONENT_KEYS.COMPANY_TYPE:
        return "Company type";
      case COMPONENT_KEYS.PRINCIPLE_BROKER:
        return "Principal Broker";
      case COMPONENT_KEYS.FINANCIAL_LOCATION:
        return "Financial Records Location";
      case COMPONENT_KEYS.APPOINTED_AGENT:
        return "Appointed Agent";
      case COMPONENT_KEYS.FISCAL_END_DATE:
        return "Fiscal End Date";
      case COMPONENT_KEYS.CRIMINAL_RECORD:
        return "Criminal Record";
      case COMPONENT_KEYS.PROOF_INSURANCE_OMISSIONS:
        return "Proof of Errors and Omissions Insurance";
      default:
        return "";
    }
  };

  const documentReviewComponent = (file) => {
    const fileComponents = [];

    if (file.documentContentType === DOCUMENT_CONTENT_TYPE_PDF) {
      fileComponents.push(
        <div style={{ maxWidth: "40rem", overflow: "hidden" }}>
          <DocumentViewerFileNameLink
            id={"document-view-link-" + file.fileName}
            key={"document-view-link-" + file.fileName}
            file={file}
            onClick={() => {
              if (file.data) {
                let fileDataUrl = convertFileDataForUrl(
                  file.data,
                  file.documentContentType
                );
                setDocumentViewerDocument(fileDataUrl);
                setSelectedFile(file);
                setOpenDocumentViewer(true);
              } else {
                // download file data and then show it
                dispatch(fetchDocumentData(file.guid))
                  .then(downloadFile => {
                      if(downloadFile) { 
                          let fileDataUrl = convertFileDataForUrl(downloadFile.data, downloadFile.documentContentType)
                          setDocumentViewerDocument(fileDataUrl)
                          setSelectedFile(downloadFile)
                          setOpenDocumentViewer(true)
                      }
                  })
                  .catch((error) => {
                      console.error(error)
                  })
              }
            }}
          />
        </div>
      );
    } else {
      fileComponents.push(
        <DownloadFileNameLink
          id={"download-link-" + file.fileName}
          componentKey={"download-link-" + file.fileName}
          file={file}
        />
      );
    }

    return fileComponents;
  };

  /*=== click handlers == */
  const handleEditMraCompliance = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.MRA_COMPLIANCE);
  }

  const handleEditAirReport = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.AIR_REPORT);
  }

  const handleEditCompanyType = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.COMPANY_TYPE);
  }

  const handleEditFinancialLocation = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.FINANCIAL_LOCATION);
  };

  const handleEditAppointedAgent = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.APPOINTED_AGENT);
  };

  const handleEditFiscalEndDate = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.FISCAL_END_DATE);
  };

  const handleEditCriminalRecord = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.CRIMINAL_RECORD);
  };

  const handleEditProofInsurance = () => {
    setShowEditDialog(true);
    setEditComponent(COMPONENT_KEYS.PROOF_INSURANCE_OMISSIONS);
  };

  function getPeopleCRCList() {

    if (!peopleList || peopleList.length === 0) {
      return null;
    }

    const noChangeIds = mraRenewalCrcNoChangeIds.split(",")
    const updatedFileIds = mraRenewalCrcUpdatedIds.split(",")

    const findLatestDocument = (docList, entityId) => {
      if(!docList) {
        return null
      }
      
      const filteredAndSortedDocs = docList.filter(
          document => +document.entityId === +entityId).sort(
            (document1, document2) => {
              const create1 = document1.createdDate ?? Date.now()
              const create2 = document2.createdDate ?? Date.now()
              return (create1 < create2) ? 1 : -1
            })

        if(filteredAndSortedDocs.length > 0) {
          return filteredAndSortedDocs[0]
        }

        return null
    }

    const crcSectionList = [];

    peopleList.forEach((rjscOfficer, i) => {
      const { entityId } = rjscOfficer

      let newlyUploadedCRC = false
      let noChangeInCRC = false

      if(noChangeIds.findIndex(id => +id === +entityId) !== -1) {
        noChangeInCRC = true
      } else if (updatedFileIds.findIndex(id => +id === +entityId) !== -1) {
        newlyUploadedCRC = true
      }

      const latestDocument = findLatestDocument(uploadCriminalRecordCheck, entityId)

      crcSectionList.push(
        <div key={i}>
          <ReviewForm
            data={{
              "Name": fullName(rjscOfficer),
              "Relationship": rjscOfficer?.position,
              "File": latestDocument ? documentReviewComponent(latestDocument) : "No file uploaded"
            }}
          />
          {noChangeInCRC && <div>No change to CRC or not required to upload CRC.</div>}
          {newlyUploadedCRC && <div>New file uploaded in renewal.</div>}
          <SubSeparator />
        </div>
      )
    })

    return crcSectionList;
  }

  return (
    <>
      <div className={"container"}>
        <TopNavigationBar {...props} />
        <div className="row">
          <div className="col-xs-12 col-lg-9">
            <Header
              id="broker-application-review-header"
              header="Mortgage Brokerage Licence renewal"
            />
            <Paragraph
              id="broker-application-review-text"
              text={[
                "Please review and ensure the information you have provided is correct.",
              ]}
            />
          </div>
        </div>

        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{marginLeft: "15px"}}>
              <Paragraph
                  style={{ paddingTop: "10px" }}
                  id="broker-application-review-text"
                  text={["<h3>Mortgage Brokerage MRA Compliance<h3>"]}
              />
              <ReviewForm data={{
                  "Are you in compliance with the MRA?" : agencyApplication.businessLicenceData.isMRACompliance ? 'Yes' : 'No'
                  }} 
              />
          </div>
          <div className="col-lg-3">
              <Button
                style={{ marginTop: "10px", textDecoration: "underline"}}
                buttonType={"regular_link"}
                text={"Update MRA compliance"}
                onClick={handleEditMraCompliance}
              />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{marginLeft: "15px"}}>
              <Paragraph
                  style={{ paddingTop: "10px"}}
                  id="broker-application-review-text"
                  text={["<h3>Annual Information Return (AIR)<h3>"]}
              />
              <ReviewForm data={{
                  "Has the AIR been submitted" : agencyApplication.businessLicenceData.isAIRSubmitted ? 'Yes' : 'No',
                  // "Uploaded AIR File:":
                  // uploadedAIRDocument.length > 0
                  //   ? documentReviewComponent(
                  //     uploadedAIRDocument[0]
                  //     )
                  //   : ""
                  }} 
              />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline"}}
              buttonType={"regular_link"}
              text={"Update AIR Status"}
              onClick={handleEditAirReport}
            />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{marginLeft: "15px"}}>
              <Paragraph
                  style={{ paddingTop: "10px" }}
                  id="broker-application-review-text"
                  text={["<h3>Company type<h3>"]}
              />
              <ReviewForm data={{
                  "Company type selected" : COMPANY_TYPES[agencyApplication.businessLicenceData.companyType]
                  }} 
              />
          </div>
          <div className="col-lg-3">
              <Button
              style={{ marginTop: "10px", textDecoration:"underline"}}
              buttonType={"regular_link"}
              text={"Update Company type"}
              onClick={handleEditCompanyType}
              />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{marginLeft: "15px"}}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Financial Record Location<h3>"]}
            />
            <ReviewForm data={{
              "Civic address" : financialRecordsLocation?.street ?? '',
              "City/Town" : financialRecordsLocation?.city ?? '',
              "Province" : financialRecordsLocation?.province ?? '',
              "Postal code" : financialRecordsLocation?.postalCode ?? '',
            }} />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline"}}
              buttonType={"regular_link"}
              text={"Update financial record location"}
              onClick={handleEditFinancialLocation}
            />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{marginLeft: "15px"}}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Appointed agent<h3>"]}
            />
            <ReviewForm data={{
              "Name" : fullName(responsibleAgent)
            }} />
            <LineSeperator />
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Service address<h3>"]}
            />
            <ReviewForm data={{
              "Civic address" : serviceBusinessAddress?.street ?? "",
              "City/Town" : serviceBusinessAddress?.city ?? '',
              "Province" : serviceBusinessAddress?.province ?? '',
              "Postal code" : serviceBusinessAddress?.postalCode ?? ''
            }} />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline"}}
              buttonType={"regular_link"}
              text={"Update appointed agent"}
              onClick={handleEditAppointedAgent}
            />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{marginLeft: "15px"}}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Fiscal year end date<h3>"]}
            />
            <ReviewForm
              data={{ "Date": formatDate(companyFiscalEndDate) }}
            />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration: "underline"}}
              buttonType={"regular_link"}
              text={"Update fiscal end date"}
              onClick={handleEditFiscalEndDate}
            />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{marginLeft: "15px"}}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Errors and Omissions Insurance<h3>"]}
            />
            <ReviewForm
              data={{
                //TODO: No should hide the file display
                "Do you have Errors and Omissions insurance?" :
                  agencyApplication.businessLicenceData.hasActiveErrorsOmissionsInsurance ? 'Yes' : 'No',
                "Continuation certificate:":
                  uploadedProofOfInsuranceContinuation.length > 0
                    ? documentReviewComponent(
                        uploadedProofOfInsuranceContinuation[0]
                      )
                    : "No document uploaded",
                "Continuation certificate expiry:":
                  humanReadableDate(agencyApplication.businessLicenceData
                    .insuranceContinuationExpiryDate)
              }}
            />
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration:"underline"}}
              buttonType={"regular_link"}
              text={"Update Errors and Omissions Insurance certificate"}
              onClick={handleEditProofInsurance}
            />
          </div>
        </div>
        <MainSeparator />
        <div className="row">
          <div className="col-lg-10" style={{marginLeft: "15px"}}>
            <Paragraph
              style={{ paddingTop: "10px" }}
              id="broker-application-review-text"
              text={["<h3>Criminal Record Checks<h3>"]}
            />
            {getPeopleCRCList()}
          </div>
          <div className="col-lg-3">
            <Button
              style={{ marginTop: "10px", textDecoration:"underline"}}
              buttonType={"regular_link"}
              text={"Update criminal record check"}
              onClick={handleEditCriminalRecord}
            />
          </div>
        </div>

        <div className="row topMarginLarge">
          <div className="col-lg-4">
            <Button
              id="complete-mortgage-education-continue-button"
              className="btn-lg btn-default"
              buttonType="regular_primary"
              text="Yes, this information is correct"
              onClick={() => {
                // Set MORTGAGE PROP value
                dispatch(
                  setMortgageProperty({
                    key: MORTGAGE_APPLICATION_PROPERTY,
                    value: true,
                  })
                );

                dispatch(
                  saveBusinessLicenceApplicationData(
                    applicationType,
                    applicationMode
                  )
                )
                  .then((data) => {
                    // On to the next item
                    dispatch(push({
                        pathname : `${ROUTE_PATH.MRA_APP}${ROUTE_PATH.MRA_RENEWAL_CONFIRM_BEFORE_PAYMENT}`,
                        state: {applicationId : agencyApplication.id} 
                    }))
                  })
                  .catch((err) => {
                    // TODO : error check
                  });
              }}
            />
          </div>
        </div>
    </div>

      <Modal
        appElement={document.getElementById("root")}
        isOpen={showEditDialog}
        contentLabel="editDetailsDialog"
        onRequestClose={() => {
          setShowEditDialog(false);
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        className="modalDialogContent editDetailsModalDialog"
        closeTimeoutMS={150}
      >
        <div className="row">
          <div className="col-11">
            <div className="row">
              <div className="col-12">
                <h2>{titleForComponent(editComponent)}</h2>
                <MainSeparator />
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.MRA_COMPLIANCE && (
                  <ContentAwareness
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.AIR_REPORT && (
                  <ConfirmAIRSubmission
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.COMPANY_TYPE && (
                  <ConfirmCompanyType
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.COMPANY_CONTACT && (
                  <CompanyContactInformation
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>
            
            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.FINANCIAL_LOCATION && (
                  <ConfirmCompanyFinancialLocation
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.APPOINTED_AGENT && (
                  <ConfirmAppointedAgentServiceAddress
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.FISCAL_END_DATE && (
                  <ConfirmCompanyFiscalEndDate
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.CRIMINAL_RECORD && (
                  <ConfirmCriminalRecordCheck
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                {editComponent === COMPONENT_KEYS.PROOF_INSURANCE_OMISSIONS && (
                  <ConfirmInsuranceProof
                    isModal
                    viewMode={VIEW_MODE_EDIT_PREVIEW_BEFORE_SUBMIT}
                    onEditSave={() => setShowEditDialog(false)}
                    {...props}
                  />
                )}
              </div>
            </div>

          </div>
          <div className="col-1">
            <div
              className="closeButton pull-right"
              onClick={() => {
                setShowEditDialog(false);
              }}
            >
              <i className="fa fa-2x fa-times"></i>
            </div>
          </div>
        </div>
      </Modal>

      <DocumentViewer
        fileData={documentViewerDocument}
        fileName={selectedFile?.filename}
        isOpen={openDocumentViewer}
        handleClose={() => setOpenDocumentViewer(false)}
      />
    </>
  );
};
